/**
 * Dados mocados da Comunicacao (canais + conversa).
 *
 * Referencia temporal do mock: quinta-feira, 6 de agosto de 2026 — os rotulos
 * "Hoje"/"Ontem" das mensagens sao fixos por isso, nao calculados.
 * Regra de coerencia: `unread` de um canal e sempre igual a quantidade de
 * mensagens NAO minhas no fim da thread, e `lastMessagePreview` repete o texto
 * da ultima mensagem.
 *
 * Os tipos criaveis de canal sao os quatro do protótipo
 * (`docs/design-slices/_shell.html:2995-2999`): Time, Produto, Empresa e
 * Projeto. `pessoa` existe apenas para conversas diretas — nao aparece no
 * drawer de criacao. Canais de avisos ("Canais" do rail) moram em
 * `mocks/broadcast.ts` e grupos em `mocks/groups.ts`.
 */

/** Tipos criaveis + `pessoa`, que so a conversa direta usa. */
export type ChannelKind = 'time' | 'produto' | 'empresa' | 'projeto' | 'pessoa'

export type Presence = 'online' | 'ausente' | 'ocupado' | 'offline'

export type ChannelKindMeta = {
  kind: ChannelKind
  label: string
  icon: string
  hint: string
}

/** Os quatro tipos oferecidos na criacao — protótipo `_shell.html:2995-2999`. */
export const CHANNEL_KINDS: readonly ChannelKindMeta[] = [
  { kind: 'time', label: 'Time', icon: 'fas fa-user-friends', hint: 'Conversa fixa de um time.' },
  { kind: 'produto', label: 'Produto', icon: 'fas fa-cube', hint: 'Conversa de um produto do portfólio.' },
  { kind: 'empresa', label: 'Empresa', icon: 'fas fa-building', hint: 'Conversa de uma empresa.' },
  { kind: 'projeto', label: 'Projeto', icon: 'fas fa-folder-open', hint: 'Conversa de uma entrega ou projeto.' },
]

/** Meta da conversa direta — fora da grade de criacao. */
export const PERSON_KIND: ChannelKindMeta = {
  kind: 'pessoa',
  label: 'Pessoa',
  icon: 'fas fa-user',
  hint: 'Conversa entre duas pessoas.',
}

export const PRESENCE_LABEL: Record<Presence, string> = {
  online: 'Disponível',
  ausente: 'Ausente',
  ocupado: 'Ocupado',
  offline: 'Offline',
}

export type ChatChannel = {
  id: string
  name: string
  kind: ChannelKind
  description: string
  /** Sobrepoe o icone do tipo (grupos e canais de avisos usam icone proprio). */
  icon: string | null
  /** Nomes dos participantes visiveis no cabecalho da conversa. */
  members: readonly string[]
  memberCount: number
  unread: number
  /** Alguem citou a pessoa logada — alimenta o chip "Menções" do explorer. */
  mentionsMe: boolean
  lastMessagePreview: string
  /** Ja formatado: "09:03" para hoje, "05/08" para dias anteriores. */
  lastMessageAt: string
  /** Só faz sentido em conversa direta; nos demais fica `null`. */
  presence: Presence | null
  /** Id da reuniao em andamento vinda de `mocks/meetings.ts`. */
  liveMeetingId: string | null
  contextCode: string | null
  contextLabel: string | null
  /** Canal de leitura: o compositor vira aviso de "sem permissão". */
  readOnly: boolean
}

export type ChatMessage =
  | { id: string; kind: 'date'; text: string }
  | { id: string; kind: 'system'; text: string; contextCode: string | null }
  | {
      id: string
      kind: 'message'
      author: string
      initials: string
      text: string
      time: string
      mine: boolean
      read: boolean
    }

export const MOCK_ME = { id: 'col-cristian-ramos', name: 'Cristian Ramos', initials: 'CR' } as const

export const MOCK_CHANNELS: readonly ChatChannel[] = [
  {
    id: 'ch-ops-jk',
    name: 'Operações — JK Iguatemi',
    kind: 'projeto',
    description: 'Rollout do ambiente do cliente JK Iguatemi.',
    icon: null,
    members: ['Mariana Bezerra', 'Rafael Andrade', 'Juliana Prado', 'Cristian Ramos'],
    memberCount: 8,
    unread: 3,
    mentionsMe: true,
    lastMessagePreview: 'Juliana Prado: Fecho o roteiro e mando aqui antes das 10h.',
    lastMessageAt: '09:03',
    presence: null,
    liveMeetingId: 'mtg-revisao-jk',
    contextCode: 'DMD-0047',
    contextLabel: 'DMD-0047 · Virada de ambiente JK Iguatemi',
    readOnly: false,
  },
  {
    id: 'ch-squad-plataforma',
    name: 'Squad Plataforma',
    kind: 'time',
    description: 'Time responsável pelo núcleo da plataforma.',
    icon: null,
    members: ['Thiago Nogueira', 'Camila Duarte', 'Cristian Ramos'],
    memberCount: 6,
    unread: 0,
    mentionsMe: false,
    lastMessagePreview: 'Você: Subi a migration na branch de homologação.',
    lastMessageAt: '08:24',
    presence: null,
    liveMeetingId: null,
    contextCode: null,
    contextLabel: null,
    readOnly: false,
  },
  {
    id: 'ch-wi-0186',
    name: 'WI-0186 · Integração de pagamentos',
    kind: 'produto',
    description: 'Conversa vinculada ao Item de Trabalho da integração.',
    icon: null,
    members: ['Camila Duarte', 'Thiago Nogueira', 'Cristian Ramos'],
    memberCount: 4,
    unread: 4,
    mentionsMe: true,
    lastMessagePreview: 'Camila Duarte: Consigo revisar os critérios de aceite hoje à tarde.',
    lastMessageAt: '08:57',
    presence: null,
    liveMeetingId: null,
    contextCode: 'WI-0186',
    contextLabel: 'WI-0186 · Integração de pagamentos',
    readOnly: false,
  },
  {
    id: 'ch-empresa-brasilia',
    name: 'Brasília Shopping',
    kind: 'empresa',
    description: 'Conta do cliente Brasília Shopping.',
    icon: null,
    members: ['Juliana Prado', 'Rafael Andrade', 'Cristian Ramos'],
    memberCount: 5,
    unread: 1,
    mentionsMe: false,
    lastMessagePreview: 'Rafael Andrade: A empresa aprovou o cronograma da campanha de retenção.',
    lastMessageAt: 'Ontem',
    presence: null,
    liveMeetingId: null,
    contextCode: 'DMD-0052',
    contextLabel: 'DMD-0052 · Revisão de SLA Brasília Shopping',
    readOnly: false,
  },
  {
    id: 'ch-mariana-bezerra',
    name: 'Mariana Bezerra',
    kind: 'pessoa',
    description: 'Conversa direta.',
    icon: null,
    members: ['Mariana Bezerra', 'Cristian Ramos'],
    memberCount: 2,
    unread: 0,
    mentionsMe: false,
    lastMessagePreview: 'Você: Combinado, te chamo depois da reunião.',
    lastMessageAt: '07:58',
    presence: 'online',
    liveMeetingId: null,
    contextCode: null,
    contextLabel: null,
    readOnly: false,
  },
  {
    id: 'ch-rafael-andrade',
    name: 'Rafael Andrade',
    kind: 'pessoa',
    description: 'Conversa direta.',
    icon: null,
    members: ['Rafael Andrade', 'Cristian Ramos'],
    memberCount: 2,
    unread: 0,
    mentionsMe: false,
    lastMessagePreview: 'Nenhuma mensagem ainda',
    lastMessageAt: '',
    presence: 'ausente',
    liveMeetingId: null,
    contextCode: null,
    contextLabel: null,
    readOnly: false,
  },
]

export const MOCK_MESSAGES: Record<string, readonly ChatMessage[]> = {
  'ch-ops-jk': [
    { id: 'm-jk-01', kind: 'date', text: 'Quarta-feira, 5 de agosto' },
    {
      id: 'm-jk-02',
      kind: 'message',
      author: 'Mariana Bezerra',
      initials: 'MB',
      text: 'Pessoal, o cliente confirmou a virada do ambiente para sexta. Precisamos fechar o checklist de operação ainda hoje.',
      time: '09:12',
      mine: false,
      read: true,
    },
    {
      id: 'm-jk-03',
      kind: 'message',
      author: 'Cristian Ramos',
      initials: 'CR',
      text: 'Fechado. Subi o checklist no WI-0186 e marquei os dois critérios de aceite que ainda faltam.',
      time: '09:15',
      mine: true,
      read: true,
    },
    {
      id: 'm-jk-04',
      kind: 'message',
      author: 'Rafael Andrade',
      initials: 'RA',
      text: 'Consigo revisar o roteiro de rollback até as 11h e devolvo com os pontos de atenção.',
      time: '09:20',
      mine: false,
      read: true,
    },
    {
      id: 'm-jk-05',
      kind: 'system',
      text: 'Automação: a demanda DMD-0047 mudou para “Em execução”.',
      contextCode: 'DMD-0047',
    },
    { id: 'm-jk-06', kind: 'date', text: 'Hoje' },
    {
      id: 'm-jk-07',
      kind: 'message',
      author: 'Juliana Prado',
      initials: 'JP',
      text: 'Bom dia! O time de infraestrutura liberou a janela das 22h às 02h.',
      time: '08:41',
      mine: false,
      read: true,
    },
    {
      id: 'm-jk-08',
      kind: 'message',
      author: 'Cristian Ramos',
      initials: 'CR',
      text: 'Perfeito. Atualizei a pauta da reunião de revisão com essa janela.',
      time: '08:47',
      mine: true,
      read: true,
    },
    {
      id: 'm-jk-09',
      kind: 'message',
      author: 'Mariana Bezerra',
      initials: 'MB',
      text: '@Cristian abri a reunião de revisão de escopo aqui no canal, entra quando puder.',
      time: '08:52',
      mine: false,
      read: false,
    },
    {
      id: 'm-jk-10',
      kind: 'message',
      author: 'Rafael Andrade',
      initials: 'RA',
      text: 'Rollback revisado: só falta confirmar o backup do banco com a operação do cliente.',
      time: '08:58',
      mine: false,
      read: false,
    },
    {
      id: 'm-jk-11',
      kind: 'message',
      author: 'Juliana Prado',
      initials: 'JP',
      text: 'Fecho o roteiro e mando aqui antes das 10h.',
      time: '09:03',
      mine: false,
      read: false,
    },
  ],
  'ch-squad-plataforma': [
    { id: 'm-sp-01', kind: 'date', text: 'Hoje' },
    {
      id: 'm-sp-02',
      kind: 'message',
      author: 'Thiago Nogueira',
      initials: 'TN',
      text: 'Alguém já olhou o tempo de resposta do endpoint de bootstrap? Subiu uns 200ms depois de ontem.',
      time: '08:05',
      mine: false,
      read: true,
    },
    {
      id: 'm-sp-03',
      kind: 'message',
      author: 'Camila Duarte',
      initials: 'CD',
      text: 'É o carregamento dos catálogos sem cache. Já abri um Item de Trabalho para tratar.',
      time: '08:11',
      mine: false,
      read: true,
    },
    {
      id: 'm-sp-04',
      kind: 'message',
      author: 'Camila Duarte',
      initials: 'CD',
      text: 'Coloquei o item na lane de revisão para alguém pegar hoje.',
      time: '08:12',
      mine: false,
      read: true,
    },
    {
      id: 'm-sp-05',
      kind: 'message',
      author: 'Cristian Ramos',
      initials: 'CR',
      text: 'Subi a migration na branch de homologação.',
      time: '08:24',
      mine: true,
      read: true,
    },
  ],
  'ch-wi-0186': [
    { id: 'm-wi-01', kind: 'date', text: 'Hoje' },
    {
      id: 'm-wi-02',
      kind: 'message',
      author: 'Cristian Ramos',
      initials: 'CR',
      text: 'Deixei os três critérios de aceite descritos no item. O terceiro depende do retorno do adquirente.',
      time: '08:30',
      mine: true,
      read: true,
    },
    {
      id: 'm-wi-03',
      kind: 'system',
      text: 'Automação: o Item de Trabalho WI-0186 entrou na lane “Em revisão”.',
      contextCode: 'WI-0186',
    },
    {
      id: 'm-wi-04',
      kind: 'message',
      author: 'Thiago Nogueira',
      initials: 'TN',
      text: 'O sandbox do adquirente voltou a responder. Consigo testar o fluxo de estorno hoje.',
      time: '08:44',
      mine: false,
      read: false,
    },
    {
      id: 'm-wi-05',
      kind: 'message',
      author: 'Camila Duarte',
      initials: 'CD',
      text: '@Cristian vou precisar do log das últimas tentativas para comparar com o esperado.',
      time: '08:49',
      mine: false,
      read: false,
    },
    {
      id: 'm-wi-06',
      kind: 'message',
      author: 'Thiago Nogueira',
      initials: 'TN',
      text: 'Mando o arquivo assim que o teste terminar.',
      time: '08:53',
      mine: false,
      read: false,
    },
    {
      id: 'm-wi-07',
      kind: 'message',
      author: 'Camila Duarte',
      initials: 'CD',
      text: 'Consigo revisar os critérios de aceite hoje à tarde.',
      time: '08:57',
      mine: false,
      read: false,
    },
  ],
  'ch-empresa-brasilia': [
    { id: 'm-bs-01', kind: 'date', text: 'Ontem' },
    {
      id: 'm-bs-02',
      kind: 'message',
      author: 'Juliana Prado',
      initials: 'JP',
      text: 'A operação do shopping pediu para antecipar a revisão de SLA para a próxima terça.',
      time: '16:20',
      mine: false,
      read: true,
    },
    {
      id: 'm-bs-03',
      kind: 'message',
      author: 'Cristian Ramos',
      initials: 'CR',
      text: 'Consigo remarcar. Vou atualizar a demanda DMD-0052 com a nova data.',
      time: '16:44',
      mine: true,
      read: true,
    },
    {
      id: 'm-bs-04',
      kind: 'message',
      author: 'Rafael Andrade',
      initials: 'RA',
      text: 'A empresa aprovou o cronograma da campanha de retenção.',
      time: '17:03',
      mine: false,
      read: false,
    },
  ],
  'ch-mariana-bezerra': [
    { id: 'm-mb-01', kind: 'date', text: 'Hoje' },
    {
      id: 'm-mb-02',
      kind: 'message',
      author: 'Mariana Bezerra',
      initials: 'MB',
      text: 'Consegue participar da revisão de escopo às 09:30? Preciso da sua leitura sobre o risco da janela.',
      time: '07:52',
      mine: false,
      read: true,
    },
    {
      id: 'm-mb-03',
      kind: 'message',
      author: 'Cristian Ramos',
      initials: 'CR',
      text: 'Combinado, te chamo depois da reunião.',
      time: '07:58',
      mine: true,
      read: true,
    },
  ],
  'ch-rafael-andrade': [],
}

/** Iniciais para o avatar: duas letras, primeira e ultima palavra do nome. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

export function channelKindMeta(kind: ChannelKind): ChannelKindMeta {
  if (kind === 'pessoa') return PERSON_KIND
  return CHANNEL_KINDS.find((k) => k.kind === kind) ?? (CHANNEL_KINDS[0] as ChannelKindMeta)
}

/** Icone efetivo da conversa: o proprio, se houver, senao o do tipo. */
export function channelIcon(channel: ChatChannel): string {
  return channel.icon ?? channelKindMeta(channel.kind).icon
}
